import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import {
  connectorsByIdentifier,
  connectorTypeOf,
} from "../../../shared/connectors";
import { sharedMutators } from "../../../shared/mutators";
import { isSkillSyncInProgress } from "../../../shared/mutators/resyncSkill";
import { queries } from "../../../shared/queries";
import { principalHasSupplierPermissionFromGrants } from "../../../shared/rbac/crossResourceAvailability";
import { skills as builtinSkills } from "../../../shared/skills";
import {
  isSameGitSkillSource,
  parseGitSkillSource,
} from "../../../shared/skills/gitSource";
import {
  MAX_SKILL_NAME_LENGTH,
  skillNameError,
  uniqueSkillName,
} from "../../../shared/skills/spec";
import { useConnectorCreator } from "../../hooks/useConnectorCreator";
import { useCurrentUserGroups } from "../../hooks/useCurrentUserGroups";
import { useDefaultSkillGrants } from "../../hooks/useDefaultSkillGrants";
import { useQuery } from "../../hooks/useQuery";
import { useSkillSyncInProgress } from "../../hooks/useSkillSyncInProgress";
import { connectorNameWithAuth } from "../../lib/connector";
import { Button } from "../Button";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { ConnectorSelect } from "../ConnectorSelect";
import { Fields, LinkField, Row, ValueField } from "../GitSourceFields";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { ListTable } from "../ListTable";
import { NameBody, NameCell } from "../Monitoring/monitoringList";
import { SearchField } from "../Settings/shared";
import { SettingsCard as Card, SettingsHeading } from "../SettingsCard";
import { HeaderIconContainer } from "../SettingsLayout";
import { TabbedModal } from "../TabbedModal";
import { Text } from "../Text";
import { installGitSkillsProgress } from "./installGitSkillsProgress";

const Sections = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 8px;
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
`;

const FieldLabel = styled("div")`
  padding: 0 8px;
`;

const FieldHelper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 0 8px;
`;

const InspectRow = styled("div")`
  display: flex;
  align-items: flex-end;
  gap: 8px;

  > :first-child {
    flex: 1;
  }
`;

const CatalogHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TableWrap = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const NameInput = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

const ValidationIssues = styled("div")`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface-primary);
  border: 0.5px solid var(--red-400);
  border-radius: 10px;
`;

const ValidationIssueHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: var(--red-800);
  background: var(--red-200);
`;

const ValidationIssue = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;

  & + & {
    border-top: 0.5px solid var(--border-alpha);
  }
`;

const gitProviders = ["github", "gitlab"].flatMap(
  (identifier) => connectorTypeOf(identifier)?.variants ?? []
);

const knownGitConnectorRank: ReadonlyMap<string, number> = new Map(
  gitProviders.map((provider, index) => [provider.identifier, index])
);

const customConnector = connectorsByIdentifier.apiKey;

type Props = {
  onClose: () => void;
  onInstalled: () => void;
};

export function InstallGitSkillsModal({ onClose, onInstalled }: Props) {
  const z = useZero();
  const grants = useDefaultSkillGrants();
  const { currentUser, groupIds } = useCurrentUserGroups();
  const [apps] = useQuery(queries.connectorsIndex());
  const [existingSkills] = useQuery(queries.skillsManagementIndex());
  const [remoteUrl, setRemoteUrl] = useState("");
  const [ref, setRef] = useState("");
  const [subpath, setSubpath] = useState("");
  const [connectedAppId, setConnectedAppId] = useState<string | null>(null);
  const createConnector = useConnectorCreator(setConnectedAppId);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>(
    {}
  );
  const [search, setSearch] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [submitted, setSubmitted] = useState<Array<{
    skillId: string;
    sourcePath: string;
    name: string;
  }> | null>(null);
  const [source] = useQuery(
    queries.loadSkillGitSource({ sourceId: sourceId ?? "" }),
    { enabled: sourceId !== null }
  );

  const connectorApps = useMemo(
    () =>
      apps
        .filter((app) =>
          principalHasSupplierPermissionFromGrants(
            currentUser?.id ?? "",
            groupIds,
            app.roleGrantsAsScope,
            "connected_app:use"
          )
        )
        .sort((left, right) => {
          const leftRank =
            knownGitConnectorRank.get(left.identifier) ?? Number.MAX_VALUE;
          const rightRank =
            knownGitConnectorRank.get(right.identifier) ?? Number.MAX_VALUE;
          return leftRank - rightRank || left.name.localeCompare(right.name);
        }),
    [apps, currentUser?.id, groupIds]
  );

  const validSkills = useMemo(
    () =>
      (source?.catalog ?? []).flatMap((entry) =>
        entry.name && entry.description && entry.error === null
          ? [
              {
                path: entry.path,
                name: entry.name,
                description: entry.description,
              },
            ]
          : []
      ),
    [source?.catalog]
  );

  const visibleSkills = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return validSkills;
    return validSkills.filter(
      (skill) =>
        skill.name.toLowerCase().includes(query) ||
        skill.description.toLowerCase().includes(query) ||
        skill.path.toLowerCase().includes(query)
    );
  }, [search, validSkills]);

  const sourceSyncing = useSkillSyncInProgress(source?.syncStartedAt);
  const inspecting = sourceId !== null && (!source || sourceSyncing);
  const inspectionStale =
    source?.syncStartedAt !== null &&
    source?.syncStartedAt !== undefined &&
    !sourceSyncing;
  const ready =
    source !== undefined &&
    source.syncStartedAt === null &&
    source.lastError === null;
  const invalidEntries = (source?.catalog ?? []).filter((entry) => entry.error);
  const invalidCount = invalidEntries.length;
  const existingNames = useMemo(
    () =>
      new Set([
        ...existingSkills.map((skill) => skill.name),
        ...builtinSkills.map((skill) => skill.name),
      ]),
    [existingSkills]
  );
  const suggestedNames = useMemo(() => {
    const taken = new Set(existingNames);
    return Object.fromEntries(
      validSkills.map((skill) => {
        const name = uniqueSkillName(skill.name, taken);
        taken.add(name);
        return [skill.path, name];
      })
    );
  }, [existingNames, validSkills]);
  const alreadyInstalledPaths = useMemo(() => {
    if (!source) return new Set<string>();
    return new Set(
      existingSkills.flatMap((skill) => {
        const installation = skill.gitInstallation;
        return installation?.source &&
          isSameGitSkillSource(source, installation.source)
          ? [installation.sourcePath]
          : [];
      })
    );
  }, [existingSkills, source]);
  const selectedNameErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const selectedNames = new Set<string>();
    for (const path of selectedPaths) {
      const name = (nameOverrides[path] ?? suggestedNames[path] ?? "").trim();
      const error = skillNameError(name);
      if (error) {
        errors[path] = error;
      } else if (existingNames.has(name)) {
        errors[path] = "This name is already in use";
      } else if (selectedNames.has(name)) {
        errors[path] = "Choose a unique name";
      }
      selectedNames.add(name);
    }
    return errors;
  }, [existingNames, nameOverrides, selectedPaths, suggestedNames]);
  const hasNameErrors = Object.keys(selectedNameErrors).length > 0;
  const failedInstallations = (source?.installations ?? []).filter(
    (installation) =>
      selectedPaths.includes(installation.sourcePath) &&
      (installation.lastError !== null ||
        (installation.syncStartedAt !== null &&
          // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
          !isSkillSyncInProgress(installation.syncStartedAt, Date.now())))
  );
  const submittedInstallations = useMemo(() => {
    const submittedIds = new Set(
      (submitted ?? []).map((installation) => installation.skillId)
    );
    return (source?.installations ?? []).filter((installation) =>
      submittedIds.has(installation.skillId)
    );
  }, [source?.installations, submitted]);
  const latestSubmittedSyncStartedAt = submittedInstallations.reduce<
    number | null
  >(
    (latest, installation) =>
      installation.syncStartedAt !== null &&
      (latest === null || installation.syncStartedAt > latest)
        ? installation.syncStartedAt
        : latest,
    null
  );
  const submittedSyncing = useSkillSyncInProgress(latestSubmittedSyncStartedAt);
  const locked = installing || submitted !== null;

  const repoWebUrl = source?.remoteUrl.replace(/\.git$/, "") ?? "";
  const selectedConnector = connectorApps.find(
    (app) => app.id === connectedAppId
  );
  const selectedConnectorName = selectedConnector
    ? connectorNameWithAuth(selectedConnector)
    : null;

  useEffect(() => {
    if (!submitted || submittedSyncing) return;
    const progress = installGitSkillsProgress(
      submittedInstallations,
      submitted.length,
      Date.now()
    );
    if (progress.status === "failed") {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setActionError(progress.error);
      setInstalling(false);
      setSubmitted(null);
      return;
    }
    if (progress.status === "installed") {
      onInstalled();
    }
  }, [onInstalled, submitted, submittedInstallations, submittedSyncing]);

  const inspect = async () => {
    let parsed: ReturnType<typeof parseGitSkillSource>;
    try {
      parsed = parseGitSkillSource({
        remoteUrl,
        ref,
        subpath,
      });
    } catch (error) {
      setInputError(
        error instanceof Error ? error.message : "Invalid Git repository"
      );
      return;
    }

    try {
      if (sourceId) {
        await z.mutate(sharedMutators.deleteGitSkillSource({ sourceId }))
          .client;
      }
      const nextSourceId = nanoid(12);
      setInputError(null);
      setActionError(null);
      setSelectedPaths([]);
      setNameOverrides({});
      setSearch("");
      setSubmitted(null);
      setSourceId(nextSourceId);
      const mutation = z.mutate(
        sharedMutators.inspectGitSkillSource({
          sourceId: nextSourceId,
          remoteUrl: parsed.remoteUrl,
          ref: parsed.ref ?? null,
          subpath: parsed.subpath ?? null,
          connectedAppId,
        })
      );
      await mutation.client;
      const serverResult = await mutation.server;
      if (serverResult.type === "error") {
        throw new Error("Could not inspect repository");
      }
    } catch (error) {
      setSourceId(null);
      setInputError(
        error instanceof Error ? error.message : "Could not inspect repository"
      );
    }
  };

  const cancel = async () => {
    const previewSourceId = sourceId;
    setSourceId(null);
    try {
      if (previewSourceId) {
        await z.mutate(
          sharedMutators.deleteGitSkillSource({ sourceId: previewSourceId })
        ).client;
      }
    } finally {
      onClose();
    }
  };

  const install = async () => {
    if (!source || selectedPaths.length === 0) return;
    setInstalling(true);
    setActionError(null);
    const installations = selectedPaths.map((sourcePath) => ({
      skillId: nanoid(12),
      sourcePath,
      name: (
        nameOverrides[sourcePath] ??
        suggestedNames[sourcePath] ??
        ""
      ).trim(),
    }));
    try {
      const mutation = z.mutate(
        sharedMutators.installGitSkills({
          sourceId: source.id,
          connectedAppId: source.connectedAppId,
          installations,
          grants,
        })
      );
      await mutation.client;
      const serverResult = await mutation.server;
      if (serverResult.type === "error") {
        throw new Error("Could not install skills");
      }
      setSubmitted(installations);
    } catch (error) {
      setInstalling(false);
      setActionError(
        error instanceof Error ? error.message : "Could not install skills"
      );
    }
  };

  const retry = async () => {
    if (!source || failedInstallations.length === 0) return;
    setInstalling(true);
    setSubmitted(
      failedInstallations.map((installation) => ({
        skillId: installation.skillId,
        sourcePath: installation.sourcePath,
        name: installation.skill?.name ?? "",
      }))
    );
    setActionError(null);
    const results = await Promise.all(
      failedInstallations.map(
        (installation) =>
          z.mutate(
            sharedMutators.resyncSkill({
              skillId: installation.skillId,
              connectedAppId: source.connectedAppId,
            })
          ).server
      )
    );
    if (results.some((result) => result.type === "error")) {
      setActionError("Could not retry skill installation");
      setInstalling(false);
      setSubmitted(null);
    }
  };

  const toggle = (path: string) => {
    if (installing || submitted) return;
    setSelectedPaths((selected) =>
      selected.includes(path)
        ? selected.filter((item) => item !== path)
        : [...selected, path]
    );
  };

  return (
    <TabbedModal
      icon={
        <HeaderIconContainer>
          <Icon name="git-branch" size={16} />
        </HeaderIconContainer>
      }
      title="Install from a Git repository"
      tabs={[]}
      activeTab="install"
      onTabChange={() => {}}
      onClose={() => void cancel()}
      footerRight={
        <FooterActions>
          <Button variant="secondary" size="l" onClick={() => void cancel()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="l"
            disabled={
              !ready ||
              selectedPaths.length === 0 ||
              installing ||
              hasNameErrors ||
              (failedInstallations.length === 0 &&
                source.installations.length > 0)
            }
            onClick={() =>
              void (failedInstallations.length > 0 ? retry() : install())
            }
          >
            {installing
              ? "Installing…"
              : failedInstallations.length > 0
                ? "Retry installation"
                : selectedPaths.length === 0
                  ? "Install skills"
                  : `Install ${selectedPaths.length} ${
                      selectedPaths.length === 1 ? "skill" : "skills"
                    }`}
          </Button>
        </FooterActions>
      }
    >
      <Sections>
        <Card>
          <SettingsHeading>
            <Text variant="text-regular-md">Repository</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Discover Agent Skills in a public or private repository.
            </Text>
          </SettingsHeading>
          <FieldGroup>
            <Field>
              <FieldLabel>
                <Text variant="text-small-md">Connector</Text>
              </FieldLabel>
              <ConnectorSelect
                apps={connectorApps}
                value={connectedAppId}
                selectedName={selectedConnectorName}
                onChange={setConnectedAppId}
                placeholder="Choose connector"
                disabled={locked}
                appSubtitle={(app) =>
                  connectorsByIdentifier[app.identifier]?.authLabel
                }
                addNew={[
                  ...gitProviders.map((provider) => ({
                    label: provider.name,
                    subtitle: provider.authLabel,
                    template: {
                      name: provider.name,
                      identifier: provider.identifier,
                    },
                    onClick: () => createConnector(provider.identifier),
                  })),
                  ...(customConnector
                    ? [
                        {
                          label: customConnector.name,
                          subtitle: customConnector.tagLine,
                          template: {
                            name: customConnector.name,
                            identifier: customConnector.identifier,
                          },
                          onClick: () =>
                            createConnector(customConnector.identifier),
                        },
                      ]
                    : []),
                ]}
              />
              <FieldHelper>
                <Text variant="text-small" color="var(--text-secondary)">
                  Optional for public repositories. Known Git connectors appear
                  first, but any connector scoped to this URL can authenticate.
                </Text>
              </FieldHelper>
            </Field>
            <Field>
              <FieldLabel>
                <Text variant="text-small-md">Repository URL</Text>
              </FieldLabel>
              <InspectRow>
                <Input
                  value={remoteUrl}
                  disabled={locked}
                  placeholder="https://git.example.com/team/skills.git"
                  style={{ width: "100%", minWidth: 0 }}
                  onChange={(event) => {
                    setRemoteUrl(event.target.value);
                    setInputError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && remoteUrl.trim())
                      void inspect();
                  }}
                />
                <Button
                  variant="primary"
                  size="l"
                  disabled={!remoteUrl.trim() || inspecting || locked}
                  onClick={() => void inspect()}
                >
                  {sourceId ? "Inspect again" : "Inspect"}
                </Button>
              </InspectRow>
              <FieldHelper>
                <Text variant="text-small" color="var(--text-secondary)">
                  Enter the exact HTTPS clone URL.
                </Text>
                {inputError && (
                  <Text variant="text-small" color="var(--red-600)">
                    {inputError}
                  </Text>
                )}
              </FieldHelper>
            </Field>
            <Row>
              <Field>
                <FieldLabel>
                  <Text variant="text-small-md">Branch or tag</Text>
                </FieldLabel>
                <Input
                  value={ref}
                  disabled={locked}
                  placeholder="Default branch"
                  style={{ width: "100%", minWidth: 0 }}
                  onChange={(event) => {
                    setRef(event.target.value);
                    setInputError(null);
                  }}
                />
                <FieldHelper>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Optional.
                  </Text>
                </FieldHelper>
              </Field>
              <Field>
                <FieldLabel>
                  <Text variant="text-small-md">Repository path</Text>
                </FieldLabel>
                <Input
                  value={subpath}
                  disabled={locked}
                  placeholder="Repository root"
                  style={{ width: "100%", minWidth: 0 }}
                  onChange={(event) => {
                    setSubpath(event.target.value);
                    setInputError(null);
                  }}
                />
                <FieldHelper>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Optional path containing skills.
                  </Text>
                </FieldHelper>
              </Field>
            </Row>
          </FieldGroup>

          {source && (
            <Fields>
              <LinkField
                label="Repository"
                value={source.remoteUrl}
                href={repoWebUrl}
              />
              <Row>
                <ValueField
                  label="Branch or tag"
                  value={source.ref ?? "Default branch"}
                  title={source.ref ?? undefined}
                />
                <ValueField
                  label="Path"
                  value={source.subpath ?? "Repository root"}
                  title={source.subpath ?? undefined}
                />
                <ValueField
                  label="Commit"
                  value={
                    source.resolvedCommit ?? (inspecting ? "Inspecting…" : "—")
                  }
                  title={source.resolvedCommit ?? undefined}
                />
              </Row>
            </Fields>
          )}

          {inspecting && (
            <Text variant="text-small" color="var(--text-secondary)">
              Inspecting the repository for skills…
            </Text>
          )}
          {inspectionStale && (
            <Text variant="text-small" color="var(--red-600)">
              Inspection timed out. Inspect the repository again.
            </Text>
          )}
          {source?.lastError && (
            <Text variant="text-small" color="var(--red-600)">
              {source.lastError}
            </Text>
          )}
          {actionError && (
            <Text variant="text-small" color="var(--red-600)">
              {actionError}
            </Text>
          )}
        </Card>

        {ready && (
          <Card>
            <CatalogHeader>
              <SettingsHeading>
                <Text variant="text-regular-md">
                  {validSkills.length}{" "}
                  {validSkills.length === 1 ? "skill" : "skills"} found
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Choose the skills to install and customize their names.
                </Text>
              </SettingsHeading>
              <SearchField
                placeholder="Search skills…"
                value={search}
                onChange={setSearch}
                width={240}
              />
            </CatalogHeader>
            {invalidCount > 0 && (
              <ValidationIssues>
                <ValidationIssueHeader>
                  <Icon name="circle-off" size={16} />
                  <Text variant="text-small-md">
                    {invalidCount}{" "}
                    {invalidCount === 1 ? "entry couldn’t" : "entries couldn’t"}{" "}
                    be imported
                  </Text>
                </ValidationIssueHeader>
                {invalidEntries.slice(0, 3).map((entry) => (
                  <ValidationIssue key={entry.path}>
                    <Text variant="text-small-md">
                      {entry.path || "Repository root"}
                    </Text>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {entry.error}
                    </Text>
                  </ValidationIssue>
                ))}
                {invalidCount > 3 && (
                  <ValidationIssue>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {invalidCount - 3} more{" "}
                      {invalidCount - 3 === 1 ? "entry" : "entries"} omitted
                    </Text>
                  </ValidationIssue>
                )}
              </ValidationIssues>
            )}
            <TableWrap>
              <ListTable
                headers={{
                  name: "Skill",
                  installAs: "Install as",
                }}
                columns={{ name: "flex", installAs: "md" }}
                emptyState={
                  <Text variant="text-regular" color="var(--text-secondary)">
                    {validSkills.length === 0
                      ? "No valid skills were found."
                      : "No skills match this search."}
                  </Text>
                }
                items={visibleSkills.map((skill) => {
                  const selected = selectedPaths.includes(skill.path);
                  const alreadyInstalled = alreadyInstalledPaths.has(
                    skill.path
                  );
                  const name =
                    nameOverrides[skill.path] ??
                    suggestedNames[skill.path] ??
                    skill.name;
                  const nameError = selectedNameErrors[skill.path];
                  return {
                    key: skill.path,
                    onClick:
                      alreadyInstalled || locked
                        ? undefined
                        : () => toggle(skill.path),
                    name: (
                      <NameCell>
                        <ConnectorLogoContainer
                          size={32}
                          borderRadius={8}
                          outline={false}
                          hoverBackground={false}
                        >
                          {selected ? (
                            <Icon name="check-filled" size={16} />
                          ) : (
                            <Icon
                              name="check-empty"
                              size={16}
                              style={{ opacity: 0.3 }}
                            />
                          )}
                        </ConnectorLogoContainer>
                        <NameBody>
                          <Text variant="text-regular-md" ellipsis>
                            {skill.name}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {skill.description}
                          </Text>
                        </NameBody>
                      </NameCell>
                    ),
                    installAs: alreadyInstalled ? (
                      <Text variant="text-small" color="var(--text-secondary)">
                        Already installed
                      </Text>
                    ) : selected ? (
                      <NameInput
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Input
                          value={name}
                          disabled={locked}
                          maxLength={MAX_SKILL_NAME_LENGTH}
                          aria-label={`Name for ${skill.name}`}
                          style={{ width: "100%", minWidth: 0 }}
                          onChange={(event) =>
                            setNameOverrides((overrides) => ({
                              ...overrides,
                              [skill.path]: event.target.value,
                            }))
                          }
                        />
                        {nameError && (
                          <Text variant="text-small" color="var(--red-600)">
                            {nameError}
                          </Text>
                        )}
                      </NameInput>
                    ) : null,
                  };
                })}
              />
            </TableWrap>
          </Card>
        )}
      </Sections>
    </TabbedModal>
  );
}
